import {ModalAlert} from '../types/modal-alert';
import React, {ReactElement, useCallback, useEffect, useState} from 'react';
import VLRAFilenameTester from '../components/vlra/VLRAFilenameTester';
import TenantSelector, { TenantDisplayMetadata } from '../components/TenantSelector';
import { Accordion, Button, Card, TextField } from '@cimpress/react-components';
import { getAllPatterns } from '../clients/VLRAClient';
import { CustomPattern, FileNamePattern, PatternResponse } from '../clients/dto/VLRATypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {v4 as uuidV4} from 'uuid';
import { TestData } from '../components/vlra/util/types';
import { checkAllValuesDeep } from '../components/vlra/util/helpers';
import { EditCustomPatternDisplay } from '../components/vlra/EditCustomPatternDisplay';
import { CustomPatternDisplay } from '../components/vlra/CustomPatternDisplay';
import { StaticPatternDisplay } from '../components/vlra/StaticPatternDisplay';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';

function VLRAAdmin({showAlert} : {showAlert: (alert: ModalAlert) => void}): ReactElement {
  const [currentPattern, setCurrentPattern] = useState<CustomPattern>();
  const [isNewPattern, setIsNewPattern] = useState(false);
  const [tenantMetadata, setTenantMetadata] = useState(null as TenantDisplayMetadata | null);
  const [patterns, setPatterns] = useState<PatternResponse>();
  const [defaultTestFilename, setDefaultTestFilename] = useState<string>();
  const [filter, setFilter] = useState<string>('');
  
  const refreshData = () => {
    if (!tenantMetadata) {
      setPatterns(undefined);
      return Promise.resolve();
    }
    toast.promise(getAllPatterns(tenantMetadata.tenant), {
      loading: 'Loading all patterns',
      error: (error) => `Failed to load patterns. Error: ${error}`,
    }).then(setPatterns);
  };

  const onTestSelected = (testData: TestData) => {
    setDefaultTestFilename(testData.filename);
  };

  const onEditSelected = (pattern: CustomPattern) => {
    setIsNewPattern(false);
    setCurrentPattern(pattern);
  };

  const onNewPatternPressed = () => {
    const nextPriority = (patterns?.customPatterns?.reduce((prevValue, currentRecord) => Math.max(prevValue, currentRecord.priority), 0) ?? 0) + 1;
    const newPattern: CustomPattern = {
      id: uuidV4(),
      name: 'New Pattern',
      filenameRegex: '(?<gang>\\d+).newfile',
      examples: ['123456789.newfile'],
      eventMapping: [
        {inputEvent: 'printed', outputEventOrStatus: 'PRINTED', halfProcessedEventOrStatus: 'HALF_PRINTED'}
      ],
      priority: nextPriority,
      strategy: 'Gang',
      tenant: tenantMetadata?.tenant ?? '',
      backsideProcessing: {
        isEnabledExpression: 'true',
      }
    };
    setIsNewPattern(true);
    setCurrentPattern(newPattern);
  };

  const filterArray = useCallback(<T extends CustomPattern | FileNamePattern>(array: T[] | undefined) => {
    const filterText = filter.toLocaleLowerCase();
    const filteredArray = (array ?? []).filter(value => checkAllValuesDeep(value, filterText));
    return {
      filteredArray,
      countDisplay: `(${filteredArray.length} / ${array?.length ?? 0})`,
    };
  }, [filter]);

  useEffect(() => {
    refreshData();
  }, [tenantMetadata]);

  useEffect(() => {
    document.title = 'VLRA Admin';
  }, []);

  const {countDisplay: customPatternCount, filteredArray: customPatternsFiltered} = filterArray(patterns?.customPatterns);
  const {countDisplay: classicPatternCount, filteredArray: classicPatternsFiltered} = filterArray(patterns?.fileNamePatterns);
  const {countDisplay: doubleProcessedPatternCount, filteredArray: doubleProcessedPatternsFiltered} = filterArray(patterns?.doubleProcessedFileNamePatterns);
  const {countDisplay: halfPrintedPatternCount, filteredArray: halfPrintedPatternsFiltered} = filterArray(patterns?.halfPrintedFileNamePatterns);
  const {countDisplay: nestPatternCount, filteredArray: nestPatternsFiltered} = filterArray(patterns?.nestFileNamePatterns);
  const {countDisplay: itnPatternCount, filteredArray: itnPatternsFiltered} = filterArray(patterns?.internalTransitNbrFileNamePatterns);

  if (currentPattern) {
    return <>
      <EditCustomPatternDisplay initialPattern={currentPattern} onClose={() => setCurrentPattern(undefined)} isNewPattern={isNewPattern}/>
    </>;
  }

  return (<>
      <div className="container" style={{paddingBottom: 200}}>
        <Card header={'VLRA Admin'} className='mb-3'>
          <TenantSelector setTenantMetadata={setTenantMetadata} />
          <TextField placeholder='Filter' value={filter} onChange={c => setFilter(c.target.value)}></TextField>
        </Card>

        {patterns && <>
          <Accordion title={`Custom Patterns ${customPatternCount}`} className='mb-3' defaultOpen={true} actions={<Button variant='primary' onClick={onNewPatternPressed}><FontAwesomeIcon icon={faPlus}/> Add New Pattern</Button>}>
            {customPatternsFiltered.map(pattern => <CustomPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.id} onEditSelected={onEditSelected} showAlert={showAlert}/>)}
          </Accordion>

          <Accordion title={`Static Patterns - Classic ${classicPatternCount}`} className='mb-3'>
            {classicPatternsFiltered.map(pattern => <StaticPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.description}/>)}
          </Accordion>

          <Accordion title={`Static Patterns - Double Processed ${doubleProcessedPatternCount}`} className='mb-3'>
            {doubleProcessedPatternsFiltered.map(pattern => <StaticPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.description}/>)}
          </Accordion>

          <Accordion title={`Static Patterns - Half-Printed ${halfPrintedPatternCount}`} className='mb-3'>
            {halfPrintedPatternsFiltered.map(pattern => <StaticPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.description}/>)}
          </Accordion>

          <Accordion title={`Static Patterns - Nest ${nestPatternCount}`} className='mb-3'>
            {nestPatternsFiltered.map(pattern => <StaticPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.description}/>)}
          </Accordion>

          <Accordion title={`Static Patterns - InternalTransitNbr (ITN) ${itnPatternCount}`} className='mb-3'>
            {itnPatternsFiltered.map(pattern => <StaticPatternDisplay onTestSelected={onTestSelected} pattern={pattern} key={pattern.description}/>)}
          </Accordion>
        </>}
      </div>
      <div style={{position: 'fixed', bottom: 0, left: 0, right: 0}}>
          {patterns && <VLRAFilenameTester showAlert={showAlert} tenant={tenantMetadata?.tenant} defaultFilename={defaultTestFilename} />}
      </div>
  </>);
}

export default VLRAAdmin;
